import React, {
    useState,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useLayoutEffect,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import SalesFilters from './SalesFilters';
import axiosInstance from '../../services/api/axios';
import { useUser } from '../../context/UserContext';
import { useNavigate } from 'react-router-dom';  // Import useNavigate
import moment from 'moment';
import { Button } from '@mui/material';
import { DownloadIcon } from 'lucide-react';
import { Tooltip } from '@material-tailwind/react';

const ProjectTab = ({ project, tabId, installmentType }) => {
   // console.log("installmentType", installmentType);
    
    const [installments, setInstallments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [showPastIncome, setShowPastIncome] = useState(false);
    const [installmentTypeFilter, setInstallmentTypeFilter] = useState('all'); // 'all', 'scheduled', 'slab'
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedConfig, setSelectedConfig] = useState('all');
    const [selectedBuilding, setSelectedBuilding] = useState('all');
    const [timePeriod, setTimePeriod] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const gridRef = useRef(null);
    const [gridApi, setGridApi] = useState(null); //To allow api access in `useLayoutEffect`
    const { userInfo } = useUser();
    const customerId = userInfo.loginIdentifierDetails.customer.customerId;
    const projectId = project.projectId;
    const [showOverdue, setShowOverdue] = useState(false);
    const [activeTab, setActiveTab] = useState('future'); // 'future', 'past', 'overdue'
    const [milestoneTab, setMilestoneTab] = useState('future'); // For milestone level filtering
    const [unitTab, setUnitTab] = useState('future'); // For unit level filtering
    const [installmentTab, setInstallmentTab] = useState('future'); // For installment level filtering
    const navigate = useNavigate();  // Initialize useNavigate
    const [overdueCount, setOverdueCount] = useState(0);
    const [totalOverdueAmount, setTotalOverdueAmount] = useState(0);
    
    // New state for hierarchical view
    const [viewLevel, setViewLevel] = useState('milestones'); // 'milestones', 'units', 'installments'
    const [selectedMilestone, setSelectedMilestone] = useState(null);
    const [selectedUnit, setSelectedUnit] = useState(null);

    const apiURL = useMemo(() => {
        let url = `/customer/sales/installment/earnings?customer_id=${customerId}&project_id=${projectId}`;

        if (startDate) {
            url += `&startDate=${startDate}`;
        }
        if (endDate) {
            url += `&endDate=${endDate}`;
        }
        return url;
    }, [customerId, projectId, startDate, endDate]);


    useEffect(() => {
        const fetchInstallments = async () => {
            setLoading(true);
            setError(null);
            try {
                const response = await axiosInstance(apiURL);
               
                console.log("response", response);
                // Transform the response to flatten the data structure
                let flattenedInstallments = [];
                // console.log("response", response);
                // if (response && typeof response === 'object') {
                //     // Handle the new data structure with SLAB_WISE, SCHEDULED, etc.
                //     if (response.SLAB_WISE_PAYMENT && Array.isArray(response.SLAB_WISE_PAYMENT)) {
                //         flattenedInstallments = [...flattenedInstallments, ...response.SLAB_WISE_PAYMENT];
                        
                //     }
                //     if (response.SCHEDULED && Array.isArray(response.SCHEDULED)) {
                //         flattenedInstallments = [...flattenedInstallments, ...response.SCHEDULED];
                //     }
                //     if (response.TOKEN_PAYMENT && Array.isArray(response.TOKEN_PAYMENT)) {
                //         flattenedInstallments = [...flattenedInstallments, ...response.TOKEN_PAYMENT];
                //     }
                //     if (response.DOWN_PAYMENT && Array.isArray(response.DOWN_PAYMENT)) {
                //         flattenedInstallments = [...flattenedInstallments, ...response.DOWN_PAYMENT];
                //     }
                //     if (response.FULL_PAYMENT && Array.isArray(response.FULL_PAYMENT)) {
                //         flattenedInstallments = [...flattenedInstallments, ...response.FULL_PAYMENT];
                //     }
                    
                    // If it's already a flat array, use it directly
                    if (Array.isArray(response)) {
                        flattenedInstallments = response;
                    }
                
                // Debug: Log all unique payment types in the response
                const paymentTypes = [...new Set(flattenedInstallments.map(item => item.paymentType?.type))];
                console.log("All payment types in response:", paymentTypes);
                console.log("Total installments:", flattenedInstallments.length);
                
              
                setInstallments(flattenedInstallments);
            } catch (e) {
                setError(e);
                console.error('Failed to fetch installments:', e);
            } finally {
                setLoading(false);
            }
        };

        fetchInstallments();
    }, [project, apiURL]); // Include apiURL in dependency array

    // Helper function to categorize installments based on received status
    const categorizeInstallment = (installment) => {
        const hasReceivedAmount = installment.receivedAmount !== null && installment.receivedAmount !== undefined;
        const hasReceivedDate = installment.receivedDate !== null && installment.receivedDate !== undefined;
        const projectedDate = installment.projectedDate ? new Date(installment.projectedDate) : null;
        const now = new Date();

        if (hasReceivedAmount && hasReceivedDate) {
            return 'past';
        } else if (!hasReceivedAmount && !hasReceivedDate) {
            // Check if projected date is in the future or null
            if (!projectedDate || projectedDate >= new Date(now.toDateString())) {
                return 'future';
            } else {
                return 'overdue';
            }
        } else if (hasReceivedDate && !hasReceivedAmount) {
            return 'overdue';
        } else {
            return 'future'; // Default case
        }
    };

    const filterOverdueInstallments = useCallback(() => {
        return installments.filter((item) => {
            const category = categorizeInstallment(item);
            return category === 'overdue';
        });
    }, [installments]);

    useEffect(() => {
        const overdue = filterOverdueInstallments();
        // Calculate the overdue count whenever installments change
        setOverdueCount(overdue.length);

        // Calculate the total overdue amount
        const totalAmount = overdue.reduce((acc, item) => {
            return acc + (item.projectedAmount || 0);
        }, 0);
        setTotalOverdueAmount(totalAmount);
    }, [installments, filterOverdueInstallments]);

    // Process data for hierarchical view (milestones -> units -> installments)
    const milestonesData = useMemo(() => {
        if (!installmentType || !installmentType.includes('SLAB_WISE_PAYMENT')) {
            return [];
        }

        // Include SLAB_WISE_PAYMENT, TOKEN_PAYMENT, and DOWN_PAYMENT
        const slabRelatedInstallments = installments.filter(item => 
            item.paymentType?.type === 'SLAB_WISE_PAYMENT' ||
            item.paymentType?.type === 'TOKEN_PAYMENT' ||
            item.paymentType?.type === 'DOWN_PAYMENT'
        );

        const milestonesMap = {};
        
        slabRelatedInstallments.forEach(installment => {
            // v2 (F9/§D): milestone grouping uses wbsStage.typeName (data-driven, auto-buckets new stages)
            // For SLAB_WISE_PAYMENT, use wbsStage embed for milestone name (falls back to statusType)
            // For TOKEN_PAYMENT and DOWN_PAYMENT, use payment type as milestone name
            let milestoneName;
            if (installment.paymentType?.type === 'SLAB_WISE_PAYMENT') {
                milestoneName = installment.wbsStage?.typeName
                    || installment.statusType?.statusType?.typeName
                    || installment.statusType?.typeName
                    || 'Unknown';
            } else {
                milestoneName = installment.paymentType?.type || 'Unknown';
            }
            
            const unitId = installment.customerSales?.customerSalesId;
            const propertyNo = installment.customerSales?.propertyData?.propertyNo || 'N/A';
            
            if (!milestonesMap[milestoneName]) {
                milestonesMap[milestoneName] = {
                    milestoneName,
                    totalAmount: 0,
                    units: {}
                };
            }
            
            if (!milestonesMap[milestoneName].units[unitId]) {
                milestonesMap[milestoneName].units[unitId] = {
                    unitId,
                    propertyNo,
                    totalAmount: 0,
                    installments: []
                };
            }
            
            milestonesMap[milestoneName].totalAmount += installment.projectedAmount || 0;
            milestonesMap[milestoneName].units[unitId].totalAmount += installment.projectedAmount || 0;
            milestonesMap[milestoneName].units[unitId].installments.push(installment);
        });

        return Object.values(milestonesMap).map(milestone => ({
            ...milestone,
            units: Object.values(milestone.units)
        }));
    }, [installments, installmentType]);

    // Filter milestones data based on milestone tab
    const filteredMilestonesData = useMemo(() => {
        return milestonesData.filter(milestone => {
            // Check if milestone has any installments matching the milestone tab
            return milestone.units.some(unit => 
                unit.installments.some(installment => {
                    const category = categorizeInstallment(installment);
                    return category === milestoneTab;
                })
            );
        }).map(milestone => ({
            ...milestone,
            units: milestone.units.filter(unit => 
                unit.installments.some(installment => {
                    const category = categorizeInstallment(installment);
                    return category === milestoneTab;
                })
            ).map(unit => ({
                ...unit,
                installments: unit.installments.filter(installment => {
                    const category = categorizeInstallment(installment);
                    return category === milestoneTab;
                })
            }))
        }));
    }, [milestonesData, milestoneTab]);

    const filteredInstallments = useMemo(() => {
        let filtered = installments;

        // Debug: Show payment type distribution before date filtering
        const paymentTypeCounts = {};
        installments.forEach(item => {
            const type = item.paymentType?.type;
            paymentTypeCounts[type] = (paymentTypeCounts[type] || 0) + 1;
        });
        console.log("Payment type distribution before date filtering:", paymentTypeCounts);

        // Apply proper filtering based on received amount and date
        filtered = filtered.filter(installment => {
            const category = categorizeInstallment(installment);
            return category === activeTab;
        });

        // Debug: Show payment type distribution after date filtering
        const filteredPaymentTypeCounts = {};
        filtered.forEach(item => {
            const type = item.paymentType?.type;
            filteredPaymentTypeCounts[type] = (filteredPaymentTypeCounts[type] || 0) + 1;
        });
        console.log(`Payment type distribution after ${activeTab} filtering:`, filteredPaymentTypeCounts);

        if (installmentType && Array.isArray(installmentType)) {
            console.log("Filtering by installmentType:", installmentType);
            console.log("installmentType array:", JSON.stringify(installmentType));
            const beforeFilter = filtered.length;
            console.log("Before filtering - installments count:", beforeFilter);
            
            // Log a few sample items to see their payment types
            console.log("Sample installments before filtering:", filtered.slice(0, 3).map(item => ({
                id: item.customerSalesInstallmentId,
                paymentType: item.paymentType?.type,
                projectedAmount: item.projectedAmount
            })));
            
            filtered = filtered.filter((item) => {
                const paymentType = item.paymentType?.type;
                const isIncluded = installmentType.includes(paymentType);
                console.log(`Item ${item.customerSalesInstallmentId}: paymentType="${paymentType}", included=${isIncluded}`);
                return isIncluded;
            });
            console.log(`Filtered from ${beforeFilter} to ${filtered.length} installments`);
            console.log("filtered====", filtered);
        } else {
            console.log("No installmentType filter applied - installmentType:", installmentType);
        }

        // Apply search term filter
        if (searchTerm) {
            filtered = filtered.filter((item) =>
                item.customerSales?.propertyData?.projectName
                    ?.toLowerCase()
                    .includes(searchTerm.toLowerCase())
            );
        }

        // Apply config filter
        if (selectedConfig !== 'all') {
            filtered = filtered.filter(
                (item) =>
                    item.customerSales?.propertyData?.configuration ===
                    selectedConfig
            );
        }

        // Apply building filter
        if (selectedBuilding !== 'all') {
            filtered = filtered.filter(
                (item) =>
                    item.customerSales?.propertyData?.buildingNo === selectedBuilding
            );
        }

       
        return filtered;
    }, [
        installments,
        installmentType,
        project,
        activeTab,
        filterOverdueInstallments,
        searchTerm,
        selectedConfig,
        selectedBuilding,
    ]);

    const handleView = (data) => {
        // Handle different data structures - direct installment data vs hierarchical data
        let customerSalesId, projectId;
        
        if (data.customerSales) {
            // Direct installment data
            customerSalesId = data.customerSales.customerSalesId;
            projectId = data.customerSales.propertyData?.projectPhase?.project?.projectId;
        } else if (data.customerSalesId) {
            // Hierarchical data (unit level) - using customerSalesId
            customerSalesId = data.customerSalesId;
            projectId = project.projectId;
        } else if (data.unitId) {
            // Hierarchical data (unit level) - using unitId as customerSalesId
            customerSalesId = data.unitId;
            projectId = project.projectId;
        } else {
            console.error('Unable to determine customerSalesId from data:', data);
            return;
        }
        
        if (customerSalesId && projectId) {
            navigate(`/properties/sold/${customerSalesId}?projectId=${projectId}`);
        } else {
            console.error('Missing required IDs:', { customerSalesId, projectId });
        }
    };

    const columnDefs = useMemo(() => {
        let baseColumns = [
            {
                headerName: 'Installment ID',
                field: 'customerSalesInstallmentId',
                sortable: true,
                filter: true,
                flex: 1,
            },
            {
                headerName: 'Property No',
                valueGetter: (params) =>
                    params.data.customerSales?.propertyData?.propertyNo || '',
                sortable: true,
                filter: true,
                flex: 1,
                cellRenderer: (params) => {
                    const propertyNo = params.data.customerSales?.propertyData?.propertyNo || '';
                    const customerSalesId = params.data.customerSales?.customerSalesId;

                    if (customerSalesId) {
                        return (
                            <Tooltip placement="right" contentClassName="bg-slate-800 text-white font-normal text-sm">
                            <Tooltip.Trigger>
                            <button
                                onClick={() => handleView(params.data)}
                                className="text-blue-500 hover:underline underine"
                            >
                                {propertyNo}
                            </button>
                            </Tooltip.Trigger>
                             <Tooltip.Content className="bg-slate-800 text-white font-normal text-sm">
                                         View property details
                                          <Tooltip.Arrow />
                                        </Tooltip.Content>
                        </Tooltip>
                            
                        );
                    } else {
                        return propertyNo;
                    }
                },
            },
            {
                headerName: 'Payment Type',
                field: 'paymentType.type',
                sortable: true,
                filter: true,
                flex: 1,
                cellRenderer: (params) => {
                    const paymentType = params.data.paymentType?.type;
                    // v2 (F9/§D): wbsStage.typeName replaces statusType.typeName for display
                    const stageName = params.data.wbsStage?.typeName
                        || params.data.statusType?.typeName;

                    if (paymentType === 'SLAB_WISE_PAYMENT' && stageName) {
                        return `${paymentType} (${stageName})`;
                    }

                    return paymentType || '';
                },
            },

            {
                headerName: 'Building No',
                valueGetter: (params) =>
                    params.data.customerSales?.propertyData?.buildingNo || '',
                sortable: true,
                filter: true,
                flex: 1,
            },
            {
                headerName: 'Source',
                valueGetter: (params) =>
                    params.data.customerSales?.source || '',
                sortable: true,
                filter: true,
                flex: 1,
            },
            {
                headerName: 'Projected Amount (₹)',
                field: 'projectedAmount',
                sortable: true,
                filter: true,
                flex: 1,
                valueFormatter: (params) =>
                    '₹' + (params.value ? params.value.toLocaleString() : '0'),
            },
            {
                headerName: 'Projected Date',
                field: 'projectedDate',
                sortable: true,
                filter: true,
                flex: 1,
                valueFormatter: (params) => {
                    // Format the date using moment.js
                    const date = params.value;
                    return date ? moment(date).format('YYYY-MM-DD') : '';
                },
            },
        ];

        if (activeTab !== 'future') {
            baseColumns = [
                ...baseColumns,
                {
                    headerName: 'Received Amount (₹)',
                    field: 'receivedAmount',
                    sortable: true,
                    filter: true,
                    flex: 1,
                    valueFormatter: (params) =>
                        '₹' + (params.value ? params.value.toLocaleString() : '0'),
                    cellRenderer: (params) => {
                        if (
                            activeTab === 'overdue' &&
                            params.data.receivedAmount === null
                        ) {
                            return (
                                <span style={{ color: 'red', fontWeight: 'bold' }}>
                                    Overdue
                                </span>
                            );
                        }
                        return params.value || '-';
                    },
                },
                {
                    headerName: 'Received Date',
                    field: 'receivedDate',
                    sortable: true,
                    filter: true,
                    flex: 1,
                    cellRenderer: (params) => {
                        if (activeTab === 'overdue' && !params.value) {
                            return (
                                <span style={{ color: 'red', fontWeight: 'bold' }}>
                                    Overdue
                                </span>
                            );
                        }
                        return params.value || '-';
                    },
                    valueFormatter: (params) => {
                        // Format the date using moment.js
                        const date = params.value;
                        return date ? moment(date).format('YYYY-MM-DD') : '';
                    },
                },
            ];
        }

        let statusColumn =
            installmentType && Array.isArray(installmentType) && installmentType.includes('SCHEDULED_PAYMENT')
                ? [] // Don't include the status column if SCHEDULED_PAYMENT is one of the types
                : [
                    {
                        headerName: 'Status',
                        field: 'statusType.typeName',
                        sortable: true,
                        filter: true,
                        flex: 1,
                    },
                ];

        return [...baseColumns, ...statusColumn];
    }, [installmentType, activeTab, navigate]);

 
    const uniqueBuildings = useMemo(() => {
        const buildings = new Set(['all']);
        installments.forEach((item) => {
            if (item.customerSales?.propertyData?.buildingNo) {
                buildings.add(item.customerSales.propertyData.buildingNo);
            }
        });
        return Array.from(buildings);
    }, [installments]);

    const defaultColDef = useMemo(
        () => ({
            sortable: true,
            filter: true,
            resizable: true,
        }),
        []
    );

    const onGridReady = useCallback((params) => {
        setGridApi(params.api); // set grid api
        params.api.sizeColumnsToFit();
    }, []);

    useLayoutEffect(() => {
        function handleResize() {
            gridApi.sizeColumnsToFit(); // resize columns on window size changes
        }

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize); // cleanup
    }, [gridApi]);

    const handleSwitchChange = (e) => {
        setShowPastIncome(e.target.checked);
    };

    const handleInstallmentTypeChange = (e) => {
        setInstallmentTypeFilter(e.target.value);
    };

    const getTimePeriodOptions = () => {
        if (activeTab === 'future') {
            return [
                { label: 'Select Period', value: '' },
                { label: 'Next 1 Month', value: 'next_1_month' },
                { label: 'Next 3 Months', value: 'next_3_months' },
                { label: 'Next Quarter', value: 'next_quarter' },
                { label: 'Next Year', value: 'next_year' },
            ];
        } else if (activeTab === 'past') {
            return [
                { label: 'Select Period', value: '' },
                { label: 'Past 1 Month', value: 'past_1_month' },
                { label: 'Past 3 Months', value: 'past_3_months' },
                { label: 'Past Quarter', value: 'past_quarter' },
                { label: 'Past Year', value: 'past_year' },
            ];
        } else {
            return [
                { label: 'Select Period', value: '' }, // Or any default options
            ];
        }
    };

    const handleTimePeriodChange = (e) => {
        const selectedPeriod = e.target.value;
        setTimePeriod(selectedPeriod);

        let start = null;
        let end = null;

        const today = moment();

        switch (selectedPeriod) {
            case 'next_1_month':
                start = today.clone().startOf('day');
                end = today.clone().add(1, 'month').endOf('day');
                break;
            case 'next_3_months':
                start = today.clone().startOf('day');
                end = today.clone().add(3, 'months').endOf('day');
                break;
            case 'next_quarter':
                start = today.clone().startOf('day');
                end = today.clone().add(3, 'months').endOf('quarter').endOf('day');
                break;
            case 'next_year':
                start = today.clone().startOf('day');
                end = today.clone().add(1, 'year').endOf('day');
                break;
            case 'past_1_month':
                start = today.clone().subtract(1, 'month').startOf('day');
                end = today.clone().endOf('day');
                break;
            case 'past_3_months':
                start = today.clone().subtract(3, 'months').startOf('day');
                end = today.clone().endOf('day');
                break;
            case 'past_quarter':
                start = today.clone().subtract(3, 'months').startOf('quarter').startOf('day');
                end = today.clone().endOf('day');
                break;
            case 'past_year':
                start = today.clone().subtract(1, 'year').startOf('day');
                end = today.clone().endOf('day');
                break;
            default:
                setStartDate('');
                setEndDate('');
                setTimePeriod('');
                return;
        }

        setStartDate(start ? start.format('YYYY-MM-DD') : '');
        setEndDate(end ? end.format('YYYY-MM-DD') : '');
    };

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setShowPastIncome(tab === 'past'); // Automatically set showPastIncome
        setShowOverdue(tab === 'overdue');
        setTimePeriod('');
        setStartDate('');
        setEndDate('');
        
        // Don't reset hierarchical view when changing tabs - preserve current level
        // The filtering will be handled by the useMemo dependencies
    };

    // Navigation functions for hierarchical view
    const handleMilestoneClick = (milestone) => {
        setSelectedMilestone(milestone);
        setViewLevel('units');
    };

    const handleUnitClick = (unit) => {
        setSelectedUnit(unit);
        setViewLevel('installments');
    };

    const handleBackToMilestones = () => {
        setViewLevel('milestones');
        setSelectedMilestone(null);
        setSelectedUnit(null);
    };

    const handleBackToUnits = () => {
        setViewLevel('units');
        setSelectedUnit(null);
    };

    useEffect(() => {
        // Set the initial tab to 'future' on component mount
        setActiveTab('future');
    }, []);

    // Handle case where selected milestone/unit no longer exists after filtering
    useEffect(() => {
        if (viewLevel === 'units' && selectedMilestone) {
            const milestoneExists = filteredMilestonesData.some(milestone => 
                milestone.milestoneName === selectedMilestone.milestoneName
            );
            
            if (!milestoneExists) {
                setViewLevel('milestones');
                setSelectedMilestone(null);
                setSelectedUnit(null);
            }
        } else if (viewLevel === 'installments' && selectedUnit && selectedMilestone) {
            const milestoneExists = filteredMilestonesData.some(milestone => 
                milestone.milestoneName === selectedMilestone.milestoneName
            );
            
            if (!milestoneExists) {
                setViewLevel('milestones');
                setSelectedMilestone(null);
                setSelectedUnit(null);
            } else {
                const unitExists = filteredMilestonesData
                    .find(milestone => milestone.milestoneName === selectedMilestone.milestoneName)
                    ?.units.some(unit => unit.unitId === selectedUnit.unitId);
                
                if (!unitExists) {
                    setViewLevel('units');
                    setSelectedUnit(null);
                }
            }
        }
    }, [filteredMilestonesData, viewLevel, selectedMilestone, selectedUnit]);

    const exportToCsv = useCallback(() => {
        const csvExportParams = {
            fileName: `installments_${project.projectName}.csv`,
        };
        gridRef.current.api.exportDataAsCsv(csvExportParams);
    }, [project]);

    // Column definitions for milestones AG Grid
    const milestonesColumnDefs = useMemo(() => [
        {
            headerName: 'Milestone',
            field: 'milestoneName',
            sortable: true,
            filter: true,
            flex: 1,
            cellRenderer: (params) => {
                return (
                    <button
                        onClick={() => handleMilestoneClick(params.data)}
                        className="text-blue-600 hover:text-blue-800 font-medium text-md hover:underline"
                    >
                        {params.value}
                    </button>
                );
            },
        },
        {
            headerName: 'Number of Units',
            field: 'units',
            sortable: true,
            filter: true,
            flex: 1,
            valueGetter: (params) => params.data.units.length,
        },
        {
            headerName: 'Total Recoverable',
            field: 'totalAmount',
            sortable: true,
            filter: true,
            flex: 1,
            valueFormatter: (params) => '₹' + (params.value ? params.value.toLocaleString() : '0'),
        },
    ], []);

    // Render hierarchical view components
    const renderMilestonesView = () => (
        <div className="space-y-4">
            <h3 className="text-lg font-semibold mb-4">Milestones Overview</h3>
            
            {/* Milestone Level Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        milestoneTab === 'past' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setMilestoneTab('past')}
                >
                    Past
                </button>
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        milestoneTab === 'future' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setMilestoneTab('future')}
                >
                    Future
                </button>
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        milestoneTab === 'overdue' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setMilestoneTab('overdue')}
                >
                    Overdue
                </button>
            </div>

            {/* Milestone Level Sales Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
               

                {uniqueBuildings && (
                    <select
                        value={selectedBuilding}
                        onChange={(e) => setSelectedBuilding(e.target.value)}
                        className="p-2 border rounded font-normal text-sm"
                    >
                        {uniqueBuildings.map((building) => (
                            <option key={building} value={building}>
                                {building === 'all' ? 'All Buildings' : building}
                            </option>
                        ))}
                    </select>
                )}

                <select
                    value={timePeriod}
                    onChange={handleTimePeriodChange}
                    className="p-2 border rounded font-normal text-sm"
                >
                    {getTimePeriodOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="p-2 border rounded font-normal text-sm"
                />
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="p-2 border rounded font-normal text-sm"
                />
            </div>
            
            <div
                className="ag-theme-quartz font-normal"
                style={{ height: 400, width: '100%' }}
            >
                <AgGridReact
                    rowData={filteredMilestonesData}
                    columnDefs={milestonesColumnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                />
                            </div>
                        </div>
    );

    // Column definitions for units AG Grid
    const unitsColumnDefs = useMemo(() => [
        {
            headerName: 'Property No',
            field: 'propertyNo',
            sortable: true,
            filter: true,
            flex: 1,
            cellRenderer: (params) => {
                return (
                    <div className="flex gap-2">
                        <button
                            onClick={() => handleUnitClick(params.data)}
                            className="text-blue-600 hover:text-blue-800 font-medium text-md hover:underline"
                        >
                            {params.value}
                        </button>
                        {/* <button
                            onClick={() => handleView(params.data)}
                            className="text-green-600 hover:text-green-800 font-medium text-sm hover:underline"
                        >
                            View Details
                        </button> */}
        </div>
    );
            },
        },
        {
            headerName: 'Number of Installments',
            field: 'installments',
            sortable: true,
            filter: true,
            flex: 1,
            valueGetter: (params) => params.data.installments.length,
        },
        {
            headerName: 'Total Amount',
            field: 'totalAmount',
            sortable: true,
            filter: true,
            flex: 1,
            valueFormatter: (params) => '₹' + (params.value ? params.value.toLocaleString() : '0'),
        },
    ], []);

    // Filter units data based on unit tab
    const filteredUnitsData = useMemo(() => {
        if (!selectedMilestone) return [];
        
        const milestoneData = milestonesData.find(milestone => 
            milestone.milestoneName === selectedMilestone.milestoneName
        );
        
        if (!milestoneData) return [];
        
        return milestoneData.units.filter(unit => 
            unit.installments.some(installment => {
                const category = categorizeInstallment(installment);
                return category === unitTab;
            })
        ).map(unit => ({
            ...unit,
            installments: unit.installments.filter(installment => {
                const category = categorizeInstallment(installment);
                return category === unitTab;
            })
        }));
    }, [milestonesData, selectedMilestone, unitTab]);

    const renderUnitsView = () => (
        <div className="space-y-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <button
                    onClick={handleBackToMilestones}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                    Milestones
                </button>
                <span>›</span>
                <span className="text-gray-800 font-medium">{selectedMilestone?.milestoneName}</span>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
                <h3 className="text-lg font-semibold">
                    Units in {selectedMilestone?.milestoneName}
                </h3>
            </div>
            
            {/* Unit Level Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        unitTab === 'past' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setUnitTab('past')}
                >
                    Past
                </button>
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        unitTab === 'future' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setUnitTab('future')}
                >
                    Future
                </button>
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        unitTab === 'overdue' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setUnitTab('overdue')}
                >
                    Overdue
                </button>
            </div>

            {/* Unit Level Sales Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
                

                {uniqueBuildings && (
                    <select
                        value={selectedBuilding}
                        onChange={(e) => setSelectedBuilding(e.target.value)}
                        className="p-2 border rounded font-normal text-sm"
                    >
                        {uniqueBuildings.map((building) => (
                            <option key={building} value={building}>
                                {building === 'all' ? 'All Buildings' : building}
                            </option>
                        ))}
                    </select>
                )}

                <select
                    value={timePeriod}
                    onChange={handleTimePeriodChange}
                    className="p-2 border rounded font-normal text-sm"
                >
                    {getTimePeriodOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="p-2 border rounded font-normal text-sm"
                />
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="p-2 border rounded font-normal text-sm"
                />
            </div>
            
            <div
                className="ag-theme-quartz font-normal"
                style={{ height: 400, width: '100%' }}
            >
                <AgGridReact
                    rowData={filteredUnitsData}
                    columnDefs={unitsColumnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                />
            </div>
        </div>
    );

    // Filter installments data based on installment tab
    const filteredInstallmentsData = useMemo(() => {
        if (!selectedUnit || !selectedMilestone) return [];
        
        const milestoneData = milestonesData.find(milestone => 
            milestone.milestoneName === selectedMilestone.milestoneName
        );
        
        if (!milestoneData) return [];
        
        const unitData = milestoneData.units.find(unit => 
            unit.unitId === selectedUnit.unitId
        );
        
        if (!unitData) return [];
        
        return unitData.installments.filter(installment => {
            const category = categorizeInstallment(installment);
            return category === installmentTab;
        });
    }, [milestonesData, selectedMilestone, selectedUnit, installmentTab]);

    const renderInstallmentsView = () => (
        <div className="space-y-4">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm text-gray-600 mb-4">
                <button
                    onClick={handleBackToMilestones}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                    Milestones
                </button>
                <span>›</span>
                <button
                    onClick={handleBackToUnits}
                    className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                    {selectedMilestone?.milestoneName}
                </button>
                <span>›</span>
                <span className="text-gray-800 font-medium">{selectedUnit?.propertyNo}</span>
            </div>
            
            <div className="flex items-center gap-4 mb-4">
                <h3 className="text-lg font-semibold">
                    Installments for {selectedUnit?.propertyNo}
                </h3>
            </div>
            
            {/* Installment Level Filters */}
            <div className="flex flex-wrap gap-2 mb-4">
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        installmentTab === 'past' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setInstallmentTab('past')}
                >
                    Past
                </button>
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        installmentTab === 'future' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setInstallmentTab('future')}
                >
                    Future
                </button>
                <button
                    type="button"
                    className={`px-3 py-1 text-sm font-medium rounded-md border ${
                        installmentTab === 'overdue' 
                            ? 'bg-blue-100 text-blue-700 border-blue-300' 
                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                    onClick={() => setInstallmentTab('overdue')}
                >
                    Overdue
                </button>
            </div>

            {/* Installment Level Sales Filters */}
            <div className="flex flex-wrap items-center gap-2 mb-4">
              
                
               
                {uniqueBuildings && (
                    <select
                        value={selectedBuilding}
                        onChange={(e) => setSelectedBuilding(e.target.value)}
                        className="p-2 border rounded font-normal text-sm"
                    >
                        {uniqueBuildings.map((building) => (
                            <option key={building} value={building}>
                                {building === 'all' ? 'All Buildings' : building}
                            </option>
                        ))}
                    </select>
                )}

                <select
                    value={timePeriod}
                    onChange={handleTimePeriodChange}
                    className="p-2 border rounded font-normal text-sm"
                >
                    {getTimePeriodOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                            {option.label}
                        </option>
                    ))}
                </select>

                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="p-2 border rounded font-normal text-sm"
                />
                <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="p-2 border rounded font-normal text-sm"
                />
            </div>
            
            <div
                className="ag-theme-quartz font-normal"
                style={{ height: 400, width: '100%' }}
            >
                <AgGridReact
                    ref={gridRef}
                    rowData={filteredInstallmentsData}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}
                    onGridReady={onGridReady}
                />
            </div>
        </div>
    );


    if (loading) return <div>Loading installments...</div>;
    if (error) return <div>Error: {error.message}</div>;

    return (
        <div className="project-details p-4">
          
              
            {/* Top level controls - only for non-hierarchical view */}
            {!(installmentType && installmentType.includes('SLAB_WISE_PAYMENT')) && (
                <div className="flex flex-col mb-4">
                    <div className="flex flex-col md:flex-row items-start md:items-center space-y-2 md:space-y-0 md:space-x-2 ">
                        {/* Responsive segmented buttons */}
                        <div className="w-full right-0 md:w-auto md:ml-auto flex flex-wrap md:inline-flex rounded-md shadow-xs dark:bg-gray-800">
                            <button
                                type="button"
                                className={`flex-1 md:flex-none px-2 md:px-4 py-2 md:py-2 text-sm font-medium text-gray-900  border border-gray-200 rounded-s-lg hover:bg-gray-100 hover:text-flamingo-500 focus:z-10 focus:ring-2 focus:ring-flamingo-500 focus:text-flamingo-500 dark:bg-gray-800 dark:border-gray-400 dark:text-white dark:hover:text-white dark:hover:bg-gray-400 dark:focus:ring-flamingo-500 dark:focus:text-white
                                 ${activeTab === 'past' ? 'bg-flamingo-100/80 text-flamingo-500 dark:bg-gray-400 dark:text-gray-900' : 'bg-white dark:bg-gray-800'}`}
                                onClick={() => handleTabChange('past')}
                            >
                                Past
                            </button>
                            <button
                                type="button"
                                className={`flex-1 md:flex-none px-2 md:px-4 py-2 md:py-2 text-sm font-medium text-gray-900  border-t border-b border-gray-200 hover:bg-gray-100 hover:text-flamingo-500 focus:z-10 focus:ring-2 focus:ring-flamingo-500 focus:text-flamingo-500 dark:bg-gray-800 dark:border-gray-400 dark:text-white dark:hover:text-white dark:hover:bg-gray-400 dark:focus:ring-flamingo-500 dark:focus:text-white
                                 ${activeTab === 'future' ? 'bg-flamingo-100/80 text-flamingo-500 dark:bg-gray-400 dark:text-gray-900' : 'bg-white dark:bg-gray-800'}`}
                                onClick={() => handleTabChange('future')}
                            >
                                Future
                            </button>
                            <button
                                type="button"
                                className={`flex-1 md:flex-none px-2 md:px-4 py-2 md:py-2 text-sm font-medium text-gray-900   border border-gray-200 rounded-e-lg hover:bg-gray-100 hover:text-flamingo-500 focus:z-10 focus:ring-2 focus:ring-flamingo-500 focus:text-flamingo-500 dark:bg-gray-800 dark:border-gray-400 dark:text-white dark:hover:text-white dark:hover:bg-gray-400 dark:focus:ring-flamingo-500 dark:focus:text-white
                                 ${activeTab === 'overdue' ? 'bg-flamingo-100/80 text-flamingo-500 dark:bg-gray-400 dark:text-gray-900' : 'bg-white dark:bg-gray-800'}`}
                                onClick={() => handleTabChange('overdue')}
                            >
                                Overdue
                            </button>
                        </div>
                        <button className="flex items-center gap-2 font-medium text-sm text-white bg-green-600 hover:bg-green-700 p-2 rounded shadow-md" onClick={exportToCsv}>
                                    <DownloadIcon size={18} /> Export
                                </button>
                    </div>
                    {activeTab === 'overdue' && (
                        <div className=" mt-2">
                           <span className="font-normal">Overdue Installments:</span>
                           <span className="bg-red-100 text-red-700 mx-2 px-2 py-1 rounded-md text-sm font-semibold">
            {overdueCount}
        </span>
                         <span className="font-normal ml-4">Total Overdue Amount:</span>
        <span className="bg-red-100 text-red-700 mx-2 px-2 py-1 rounded-md text-sm font-semibold">
            ₹{totalOverdueAmount.toLocaleString()}
        </span>
                        </div>
                    )}
                </div>
            )}

            {/* Conditional rendering based on installment type and view level */}
            {installmentType && installmentType.includes('SLAB_WISE_PAYMENT') ? (
                <div className="mt-4">
                    {viewLevel === 'milestones' && renderMilestonesView()}
                    {viewLevel === 'units' && renderUnitsView()}
                    {viewLevel === 'installments' && renderInstallmentsView()}
                </div>
            ) : (
                <div
                    className="ag-theme-quartz font-normal"
                    style={{ height: 500, width: '100%' }}
                >
                    <AgGridReact
                        ref={gridRef}
                        rowData={filteredInstallments}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        onGridReady={onGridReady}
                    />
                </div>
            )}
        </div>
    );
};

export default ProjectTab;